import { Alert, Box, Button, Card, CardContent, CircularProgress, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import { sendPasswordReset } from "../services/firebase";
import { createLogger } from "../utils/logger";

const logger = createLogger("PasswordReset");

interface PasswordResetProps {
  onBack?: () => void;
}

export const PasswordReset: React.FC<PasswordResetProps> = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await sendPasswordReset(email);
      setIsEmailSent(true);
      logger.info("Password reset email sent successfully");
    } catch (error: any) {
      logger.error("Error sending password reset email:", error);

      // Handle common Firebase Auth errors
      let errorMessage = "Failed to send password reset email";
      if (error.code === "auth/user-not-found") {
        errorMessage = "No account found with this email address";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <Card sx={{ maxWidth: 500, mx: "auto", mt: 4 }}>
        <CardContent>
          <Box textAlign="center">
            <Typography variant="h5" gutterBottom color="primary">
              Password Reset Email Sent
            </Typography>

            <Typography variant="body1" paragraph>
              We&apos;ve sent a password reset link to <strong>{email}</strong>
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
              Please check your email and click the link to reset your password. You may need to check your spam folder.
            </Typography>

            {onBack && (
              <Button variant="contained" onClick={onBack} sx={{ mt: 2 }}>
                Back to Sign In
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ maxWidth: 500, mx: "auto", mt: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom color="primary">
          Reset Your Password
        </Typography>

        <Typography variant="body1" paragraph>
          Enter your email address and we&apos;ll send you a link to reset your password.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField fullWidth label="Email Address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required disabled={isLoading} autoFocus />

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 3 }}>
            <Button type="submit" variant="contained" disabled={isLoading || !email.trim()} size="large">
              {isLoading ? <CircularProgress size={24} /> : "Send Reset Link"}
            </Button>

            {onBack && (
              <Button variant="outlined" onClick={onBack} disabled={isLoading} size="large">
                Back to Sign In
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
